/* ============================================================================
   ClientChatScreen (client-chat-screen D7). The client's chat with the business:
   the Secure room and History, a focused screen outside ClientShell.
   Copied from docs/mockups/client-chat-relay/client-chat-thread.html (frames 1 to 4),
   substituting only the injected per-tenant theme tokens for the mockup's literal
   colors: #FFF8EE is --text-on-accent, the cream hairlines are --fg-page-rgb. Static
   design constants (radii, fonts, the mockup's px values) are copied as-is. The back
   button is the shipped FlowBackButton; the segmented tabs are the catalog's.
   ============================================================================ */

.page {
  /* Static (non-themable) design constants, mirroring the mockup :root. */
  --r-lg: 18px;
  --font-body: var(--client-font-body, 'Outfit', system-ui, -apple-system, sans-serif);
  --font-display: var(--client-font-heading, 'EB Garamond', Georgia, serif);

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* The locked height (the largest stable viewport): the keyboard spacer below the composer
     grows into it, so nothing above the composer moves when Telegram shrinks the web view. */
  height: var(--app-viewport-stable-height, 100dvh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--bg-page);
  color: var(--text-primary);
}

/* The page atmosphere (ClientShell's .atmosphere), locked to the pre-keyboard height. */
.atmosphere {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--app-viewport-stable-height, 100dvh);
  z-index: 0;
  background:
    var(--page-overlay, none),
    var(--page-image, none),
    var(--bg-page);
  pointer-events: none;
}

.screen {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* Header: flow back button · identity · 44px spacer keeps the name centered */
.hdr {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 44px 1fr 44px;
  align-items: center;
  gap: 10px;
  padding: calc(
      var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px))
      + var(--tg-content-safe-area-inset-top, 0px)
      + 2px
    )
    18px 10px;
}

.who { display: flex; align-items: center; justify-content: center; gap: 10px; min-width: 0; }
.whoText { min-width: 0; text-align: center; }
.whoName {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 500;
  /* The studio's name carries the header on its own (Gabriel 2026-10-06: no avatar letter). */
  font-size: 24px;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.whoSub {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 3px;
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 400;
  color: var(--text-tertiary);
}
.whoSub svg { width: 10px; height: 10px; stroke-width: 2.2; color: var(--accent); }
/* The phone website's sub-line, Secure · Telegram, each with its mark (shared-client-screens M3). */
.leg { display: inline-flex; align-items: center; gap: 4px; }
/* Doubled class: outweighs `.whoSub svg` whatever order the CSS loads in. */
.leg.leg svg { width: 12px; height: 12px; flex: 0 0 auto; }
.dot { margin: 0 6px; opacity: 0.6; }

/* Segmented tabs (shipped catalog grammar) with a lock in the Secure label. They fold with
   the keyboard and return when it drops (frame 2). */
.tabWrap {
  flex-shrink: 0;
  position: relative;
  z-index: 2;
}
/* Secure: the tabs sit over the thread's top edge, which reserves their height as padding, so the
   keyboard slides them up under the header with a transform (no row collapse, nothing reflows).
   The wrap clips at the header's edge and lets touches through to the thread beneath. */
.tabOverlay { overflow: hidden; pointer-events: none; }
.tabOverlay .tabSlide {
  pointer-events: auto;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.tabOverlay[data-folded='true'] .tabSlide {
  transform: translateY(-100%);
  pointer-events: none;
}
.threadUnderTabs {
  margin-top: calc(-1 * var(--tabs-h, 0px));
  padding-top: var(--tabs-h, 0px);
}

.tabSegmented {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3px;
  padding: 3px;
  margin: 0 18px 10px;
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  border-radius: 14px;
  box-shadow: var(--glass-shadow);
  position: relative;
  overflow: hidden;
}
.tabSegmented::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, rgba(var(--accent-light-rgb), 0.16) 0%, rgba(var(--accent-light-rgb), 0.03) 100%);
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 2;
}
.tabSegment {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  background: transparent;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 14px;
  border-radius: 9px;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  color: var(--text-tertiary);
  text-align: center;
  -webkit-tap-highlight-color: transparent;
}
.tabSegment svg { width: 12px; height: 12px; stroke-width: 2.2; }
.tabSegment.active { color: var(--text-primary); }
/* The Secure tab's unread while History is open (Gabriel 2026-10-05): the Home Chat chip's badge
   (BookingHubPage .quickActionBadge) laid inline after the label. */
.tabCount {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  box-sizing: border-box;
  border-radius: 9999px;
  background: var(--accent);
  color: var(--text-on-accent);
  font-family: var(--font-body);
  font-size: 10.5px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
}
/* The selected tab's look, on the block that slides between the two (SlidingTabIndicator) */
.tabIndicator {
  position: absolute;
  top: 3px;
  bottom: 3px;
  z-index: 0;
  border-radius: 9px;
  background: rgba(var(--accent-rgb), 0.16);
  box-shadow: inset 0 0 0 1px rgba(var(--accent-rgb), 0.45), 0 0 18px rgba(var(--accent-rgb), 0.18);
  pointer-events: none;
}

/* History chips (lenses) */
.chips {
  flex-shrink: 0;
  display: flex;
  gap: 6px;
  padding: 0 18px 8px;
  overflow-x: auto;
  scrollbar-width: none;
}
.chips::-webkit-scrollbar { display: none; }
.chip {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 9999px;
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 500;
  color: var(--text-tertiary);
  background: var(--glass-bg);
  box-shadow: inset 0 0 0 1px rgba(var(--accent-light-rgb), 0.08);
  -webkit-tap-highlight-color: transparent;
}
.chip[aria-pressed='true'] {
  color: var(--accent-hot);
  background: rgba(var(--accent-rgb), 0.16);
  box-shadow: inset 0 0 0 1px rgba(var(--accent-rgb), 0.45);
}

/* The thread owns the scroll. Its content bottom-anchors so a short conversation sits above
   the composer like a real chat (.thread > :first-child { margin-top: auto }). */
.thread {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-anchor: none;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  display: flex;
  flex-direction: column-reverse;
}
.thread::-webkit-scrollbar { display: none; }
.threadInner {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 6px 18px 14px;
}
.threadInner > :first-child { margin-top: auto; }
/* History is read-only: no composer, the thread runs to the bottom edge (frame 4). */
.readOnly .threadInner { padding-bottom: calc(14px + env(safe-area-inset-bottom, 0px)); }

.day {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0 4px;
  font-family: var(--font-body);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-muted);
}
.day::before,
.day::after { content: ''; flex: 1; height: 1px; background: rgba(var(--fg-page-rgb), 0.08); }

.msg {
  position: relative;
  max-width: 82%;
  padding: 10px 13px 8px;
  border-radius: 18px;
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.38;
  letter-spacing: 0.002em;
  overflow-wrap: anywhere;
}
.text { white-space: pre-wrap; }
.them {
  align-self: flex-start;
  border-bottom-left-radius: 6px;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  box-shadow: var(--glass-shadow);
  color: var(--text-primary);
}
.them::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, rgba(var(--accent-light-rgb), 0.16) 0%, rgba(var(--accent-light-rgb), 0.03) 100%);
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}
.me {
  align-self: flex-end;
  border-bottom-right-radius: 6px;
  background: linear-gradient(160deg, var(--accent-hot) 0%, var(--accent) 55%, var(--accent-deep) 100%);
  color: var(--text-on-accent);
  box-shadow: 0 6px 18px rgba(var(--accent-shadow-rgb), 0.35);
}
.meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 4px;
  font-size: 10.5px;
  color: var(--text-tertiary);
}
.me .meta {
  /* Room for the widest state (the time and the tick; "Sending…" is about as wide), so a short
     bubble never widens and shifts its words when it turns sent (Gabriel 2026-10-06, as admin). */
  min-width: calc(4.8em + 16px);
  color: var(--text-on-accent);
  color: color-mix(in srgb, var(--text-on-accent) 70%, transparent);
}
.meta svg { width: 12px; height: 12px; stroke-width: 2.6; }
.me .meta svg { color: var(--text-on-accent); }
/* The room beside a message's time on the phone website (M7): the lock on a secure message, the Telegram plane on any other. */
.mark { display: inline-flex; margin-right: 4px; opacity: 0.75; vertical-align: -1px; }
/* Doubled class: outweighs `.meta svg`. */
.mark.mark svg { width: 11px; height: 11px; }

/* A send the server did not store: no tick, a line under the bubble (not drawn; the
   admin thread's frame 4 failure line in the warm tokens). */
.failNote {
  align-self: flex-end;
  margin-top: -4px;
  padding: 0;
  border: 0;
  background: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  color: var(--danger);
}
.failNote svg { width: 12px; height: 12px; stroke-width: 2; }
/* The failure line opens under its bubble (RevealLine): the wrap carries the note's margin and
   grows from under the thread's 8px gap. */
.failReveal {
  align-self: flex-end;
  --reveal-gap: 8px;
  --reveal-margin: -4px;
}
.failReveal .failNote { margin-top: 0; }

/* Automatic messages (History, business side): the text the client got, a footer with its door */
.auto { max-width: 86%; padding-bottom: 0; }
.autoBody { white-space: pre-line; }
.autoBody a { color: var(--accent); }
/* A long automatic message clamps at about 15 lines (ClampedBody, shared with the admin thread). */
.showMore {
  appearance: none;
  -webkit-appearance: none;
  display: block;
  margin-top: 2px;
  padding: 2px 0;
  border: 0;
  background: none;
  font-family: var(--font-body);
  font-size: 12.5px;
  font-weight: 600;
  color: var(--accent);
  -webkit-tap-highlight-color: transparent;
}
.foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 8px -13px 0;
  padding: 8px 13px 8px;
  border-top: 1px solid rgba(var(--fg-page-rgb), 0.07);
}
.footEnd { justify-content: flex-end; }
.door {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  background: none;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-family: var(--font-body);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--accent);
  -webkit-tap-highlight-color: transparent;
}
.door svg { width: 12px; height: 12px; stroke-width: 2.2; }
.foot .time { font-size: 10.5px; color: var(--text-tertiary); }
.state {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}
.state svg { width: 12px; height: 12px; stroke-width: 2.4; color: var(--accent); }
.fold {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  background: none;
  width: calc(100% + 26px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px -13px 0;
  padding: 8px 13px 0;
  border-top: 1px solid rgba(var(--fg-page-rgb), 0.07);
  font-family: var(--font-body);
  font-size: 12.5px;
  color: var(--text-secondary);
  text-align: left;
}
.fold svg { width: 12px; height: 12px; stroke-width: 2.2; color: var(--text-tertiary); transition: transform 0.32s cubic-bezier(0.32, 0.72, 0, 1); }
.fold[aria-expanded='true'] svg { transform: rotate(90deg); }
.foldPage {
  margin-top: 8px;
  white-space: pre-line;
  font-size: 13px;
  color: var(--text-secondary);
}

.sys {
  align-self: center;
  font-family: var(--font-body);
  font-size: 11.5px;
  color: var(--text-muted);
  text-align: center;
}

/* First open: the explainer card (frame 3) */
.explain {
  align-self: center;
  margin: auto 10px;
  padding: 18px 18px 16px;
  text-align: center;
  border-radius: var(--r-lg);
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  box-shadow: var(--glass-shadow);
  position: relative;
}
.explain::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, rgba(var(--accent-light-rgb), 0.16) 0%, rgba(var(--accent-light-rgb), 0.03) 100%);
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}
.explainIcon {
  width: 50px;
  height: 50px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 28%, rgba(var(--accent-hot-rgb), 0.32) 0%, rgba(var(--accent-deep-rgb), 0.16) 100%);
  border: 1px solid rgba(var(--accent-rgb), 0.22);
  display: grid;
  place-items: center;
  color: var(--accent);
}
.explainIcon svg { width: 22px; height: 22px; stroke-width: 1.7; }
.explainTitle {
  margin: 0 0 6px;
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 19px;
  letter-spacing: -0.018em;
  color: var(--text-primary);
}
.explainBody {
  margin: 0;
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.45;
  color: var(--text-secondary);
}

/* Loading, empty and error states inside the thread */
.stateRow {
  align-self: center;
  margin: auto 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--text-secondary);
  text-align: center;
}
.spinner {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 3px solid rgba(var(--accent-rgb), 0.2);
  border-top-color: var(--accent);
  animation: chat-spin 0.8s linear infinite;
}
@keyframes chat-spin { to { transform: rotate(360deg); } }
.retryBtn {
  appearance: none;
  border: 0;
  padding: 10px 18px;
  border-radius: 14px;
  background: var(--accent-orb);
  color: var(--text-on-accent);
  font-family: var(--font-body);
  font-weight: 600;
}

/* The business is typing (D11; not drawn): a quiet line above the composer. */
.typing {
  flex-shrink: 0;
  margin: 0;
  padding: 0 18px 6px;
  font-family: var(--font-body);
  font-size: 11.5px;
  color: var(--text-tertiary);
}

/* Composer (the admin composer's contract in the warm skin) */
.composer {
  position: relative;
  z-index: 3; /* the plane flies over the thread */
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px 16px calc(8px + max(22px, var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))));
  border-top: 1px solid rgba(var(--fg-page-rgb), 0.06);
}
/* Frame 2: the keyboard covers the home indicator */
.raised { padding-bottom: 8px; }
.pill {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 11px 16px;
  border-radius: 22px;
  background: var(--glass-bg);
  box-shadow: inset 0 0 0 1px rgba(var(--accent-light-rgb), 0.1);
  cursor: text;
}
.input {
  flex: 1;
  min-width: 0;
  display: block;
  margin: 0;
  padding: 0;
  border: 0;
  outline: none;
  resize: none;
  background: transparent;
  color: var(--text-primary);
  caret-color: var(--accent-hot);
  font-family: var(--font-body);
  /* 16px: iOS zooms the page into a smaller focused field. */
  font-size: 16px;
  line-height: 22px;
  overflow-y: auto;
  scrollbar-width: none;
}
.input::-webkit-scrollbar { display: none; }
.input::placeholder { color: var(--text-muted); }
.send {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: linear-gradient(to bottom, var(--accent-light) 0%, var(--accent) 50%, var(--accent-deep) 100%);
  color: var(--text-on-accent);
  box-shadow: 0 6px 18px rgba(var(--accent-shadow-rgb), 0.45);
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
/* Muted while the pill is empty (frame 1); filled once there is text (frame 2) */
.sendIdle {
  background: rgba(var(--accent-rgb), 0.18);
  color: var(--accent);
  box-shadow: none;
}
.send svg { width: 21px; height: 21px; margin-left: 1px; margin-top: -1px; overflow: visible; }
.send:disabled { cursor: default; }
/* The length limit, one line above the pill; laid over the thread so nothing moves */
.limit {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 100%;
  margin: 0;
  padding: 4px 16px;
  background: var(--bg-page);
  color: var(--danger);
  font-family: var(--font-body);
  font-size: 11.5px;
  font-weight: 600;
}

/* In-flow keyboard spacer: useKeyboardSpacerFollow drives its height per frame. */
.keyboardSpacer { flex-shrink: 0; height: 0; }
